import React, { useState, useCallback, FC } from "react";
import { useMediaQuery } from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";

import { risksService } from "../../../../../services/risks-service";

import {
    getUpdatedFormForDeleteEntity,
    getUpdatedFormForEntity,
    getUpdatedFormForMassDeleteEntity,
    getVisibleEntities,
} from "../../../../../utils/formViews";

import { CHANGE_STATUS } from "../../../../common/const";
import { EntityFormPageViews, EntityFormTabs } from "../../../../common/entity-form";
import breakpoints from "../../../../common/styles/breakpoints.js";

import RisksView from "../../../../access-rules/matrix-headers/risks-view";
import { CompensationProcedure, CompensationProcedureDetailed } from "@/types/compensation-procedures";
import { ErrorDetails } from "types/common/responses/responses-types";
import CompensatedEmployeeViewTable from "./compensated-employees-view-table";

const tabs = [
    {
        id: "risks",
        titleId: "matrix-headers-form-content.tabs.risk",
    },
    {
        id: "compensatedEmployees",
        titleId: "compensation-procedure-view-tabs.compensated-employees",
    },
];

const getUpdatedFormForRisk = (form, risk, field, value) => {
    return getUpdatedFormForEntity(form, risk, field, value, "risks", ["riskId"]);
};

const checkItemsForDuplicates = (items) => {
    const keysMap = {};
    const entityKeyFields = ["riskId"];

    const getItemKey = (i) => entityKeyFields.map((field) => i[field]).join("-");

    items.forEach((item) => {
        if (item.changeStatus === CHANGE_STATUS.DELETE) return;

        const key = getItemKey(item);

        if (!(key in keysMap)) {
            keysMap[key] = 0;
        }

        keysMap[key] += 1;
    });

    items.forEach((item) => {
        if (item.changeStatus === CHANGE_STATUS.DELETE) return;

        const key = getItemKey(item);

        if (keysMap[key] === 1) {
            entityKeyFields.forEach((field) => {
                const fieldHasError = item[`error_${field}`] && item[`error_${field}`]["code"] === "duplicate";

                if (fieldHasError) {
                    delete item[`error_${field}`];
                }
            });
        }
    });
};

const removeDuplicateError = (item) => {
    ["riskId"].forEach((field) => {
        if (item[`error_${field}`] && item[`error_${field}`].code === "duplicate") {
            item[`error_${field}`] = null;
        }
    });
};

const getValidationErrorMessage = (error) => error.detail || error.title;

const validateRisk = (item, risk, error) => {
    if (error) {
        item["error_riskId"] = {
            value: item["riskId"],
            type: "validation",
            message: getValidationErrorMessage(error),
        };

        item["description"] = "";

        return;
    }

    item["error_riskId"] = null;
    item["description"] = risk?.description ?? "";
};

type CompensationProcedureFormViewsProps = {
    form: CompensationProcedureDetailed;
    setForm: React.Dispatch<React.SetStateAction<CompensationProcedureDetailed>>;
    validationErrors: Record<string, ErrorDetails> | null;
    formDisabled: boolean;
    errorScroll: { errorIndex: number | null; errorTab: string | null } | null;
    setErrorScroll: (value: { errorIndex: number | null; errorTab: string | null }) => void;
};

const CompensationProcedureFormViews: FC<CompensationProcedureFormViewsProps> = ({
    form,
    setForm,
    formDisabled,
    validationErrors,
    errorScroll,
    setErrorScroll,
}) => {
    const matches1024 = useMediaQuery(`(min-width:  ${breakpoints.minLaptopBreakpoint})`);

    const [currentView, setCurrentView] = useState(tabs[0].id);

    const addRisk = () => {
        const risk = {
            id: nanoid(),
            riskId: "",
            description: "",
            changeStatus: CHANGE_STATUS.ADD,
            error_riskId: null,
        };

        setForm((form) => ({
            ...form,
            risks: [...form.risks, risk],
        }));
    };

    const deleteRisk = (id) => {
        setForm((form) => {
            const { newForm } = getUpdatedFormForDeleteEntity(form, id, "risks");
            newForm.risks.forEach((item) => {
                removeDuplicateError(item);
            });
            return newForm;
        });
    };

    const deleteRiskMass = useCallback(
        (ids) => {
            setForm((form) => {
                const newForm = getUpdatedFormForMassDeleteEntity(form, ids, "risks");
                newForm.risks.forEach((item) => {
                    removeDuplicateError(item);
                });
                return newForm;
            });
        },
        [setForm]
    );

    const editRisk = async (row, field, value) => {
        if (field === "active") {
            setForm((form: CompensationProcedure) => {
                const { newForm } = getUpdatedFormForRisk(form, row, field, value);
                return newForm;
            });
            return;
        }

        let risk, error;

        if (value) {
            try {
                risk = await risksService.getRiskDetailed(value);
            } catch (e) {
                error = e;
            }
        }

        const newRiskValue = risk ? risk.id : value;

        setForm((form) => {
            const { newForm, item } = getUpdatedFormForRisk(form, row, field, newRiskValue);
            checkItemsForDuplicates(newForm.risks);
            validateRisk(item, risk, error);
            return newForm;
        });
    };

    const visibleRisks = getVisibleEntities(form.risks);

    const risksErrorIndex = errorScroll?.errorTab === "risks" ? errorScroll.errorIndex : null;

    return (
        <EntityFormPageViews errorScroll={errorScroll} setCurrentView={setCurrentView}>
            <EntityFormTabs tabs={tabs} tabId={currentView} setTabId={(id) => setCurrentView(id)} oneVisibleTab={!matches1024} />

            {currentView === "risks" && (
                <RisksView
                    risks={visibleRisks}
                    addRisk={addRisk}
                    deleteRisk={deleteRisk}
                    deleteRiskMass={deleteRiskMass}
                    editRisk={editRisk}
                    disabled={formDisabled}
                    validationErrors={validationErrors}
                    errorIndex={risksErrorIndex}
                    setErrorScroll={setErrorScroll}
                    hasStatusField={false}
                />
            )}
            {currentView === "compensatedEmployees" && (
                <CompensatedEmployeeViewTable disabled={true} risks={form.compensatedEmployees} editRisk={() => {}} setErrorIndex={() => {}} />
            )}
        </EntityFormPageViews>
    );
};

export default CompensationProcedureFormViews;
